import { Fragment } from 'react';
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  EyeIcon,
  ArrowPathIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  FingerPrintIcon,
  PhoneIcon,
  ScaleIcon,
  HandRaisedIcon,
  BuildingOfficeIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const phases = [
  {
    id: 'identify',
    number: '1.1',
    name: 'Identify',
    icon: MagnifyingGlassIcon,
    description: 'Establish organization-wide cybersecurity risk management',
    activities: [
      'Risk assessment for people, data, assets, and systems',
      'Align security with business priorities',
      'Stakeholder identification and mapping',
    ],
  },
  {
    id: 'protect',
    number: '2.1',
    name: 'Protect',
    icon: ShieldCheckIcon,
    description: 'Implement safeguards for operational resilience',
    activities: [
      'Deploy containment measures',
      'Ensure business continuity',
      'Minimize incident impact',
    ],
  },
  {
    id: 'detect',
    number: '3.1-3.7',
    name: 'Detect',
    icon: EyeIcon,
    description: 'Enable rapid identification of incidents',
    subphases: [
      {
        number: '3.2',
        name: 'Lead Incident Handler Assigned',
        icon: UserGroupIcon,
      },
      {
        number: '3.3',
        name: 'Cyber Crisis Declared',
        icon: ExclamationTriangleIcon,
      },
      {
        number: '3.4',
        name: 'Send Notification to CCRT',
        icon: BoltIcon,
      },
      {
        number: '3.5',
        name: 'Open CCRT & Bridge Lines',
        icon: PhoneIcon,
      },
      {
        number: '3.6',
        name: 'Determine Containment Strategy',
        icon: ShieldCheckIcon,
      },
      {
        number: '3.7',
        name: 'Determine Forensic Needs',
        icon: FingerPrintIcon,
      },
    ],
  },
  {
    id: 'respond',
    number: '4.1-4.xx',
    name: 'Respond',
    icon: ArrowPathIcon,
    description: 'Execute immediate response actions',
    subphases: [
      {
        number: '4.2.1',
        name: 'Refine Frequency & Scope',
        icon: CogIcon,
      },
      {
        number: '4.2',
        name: 'CCRT Meetings',
        icon: UserGroupIcon,
      },
      {
        number: '4.3',
        name: 'Engage Law Enforcement',
        icon: ScaleIcon,
      },
      {
        number: '4.4',
        name: 'Request Technical Assistance',
        icon: HandRaisedIcon,
      },
      {
        number: '4.5',
        name: 'Communicate to Operating Committee',
        icon: BuildingOfficeIcon,
      },
    ],
  },
  {
    id: 'recover',
    number: '5.1-5.4',
    name: 'Recover',
    icon: ChartBarIcon,
    description: 'Restore impaired business operations',
    subphases: [
      {
        number: '5.2',
        name: 'Return to Business as Usual',
        icon: BuildingOfficeIcon,
      },
      {
        number: '5.3 & 5.4',
        name: 'Deactivation & Closure',
        icon: ChartBarIcon,
      },
    ],
  },
];

export default function ResponseFlow() {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-medium mb-6">Cyber Crisis Management Response Flow</h3>
      
      <div className="relative">
        {/* Main Flow */}
        <div className="flex justify-between items-center mb-12">
          {phases.map((phase, index) => (
            <Fragment key={phase.id}>
              <div className="flex flex-col items-center">
                <div className={clsx(
                  'w-16 h-16 rounded-full flex items-center justify-center',
                  'bg-indigo-100 text-indigo-600'
                )}>
                  <phase.icon className="w-8 h-8" />
                </div>
                <div className="text-center mt-2">
                  <div className="font-medium">{phase.name}</div>
                  <div className="text-sm text-gray-500">{phase.number}</div>
                </div>
              </div>
              {index < phases.length - 1 && (
                <div className="flex-1 h-0.5 bg-indigo-200 mx-4 relative top-8" />
              )}
            </Fragment>
          ))}
        </div>

        {/* Subphases */}
        <div className="mt-8">
          {phases.map((phase) => (
            phase.subphases && (
              <div key={`${phase.id}-subphases`} className="mb-8">
                <h4 className="font-medium mb-4">{phase.name} Phase Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {phase.subphases.map((subphase) => (
                    <div
                      key={subphase.number}
                      className="border rounded-lg p-4 flex items-start space-x-3"
                    >
                      <div className="flex-shrink-0">
                        <subphase.icon className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-medium">{subphase.name}</div>
                        <div className="text-sm text-gray-500">{subphase.number}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>

        {/* Phase Descriptions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {phases.map((phase) => (
            <div key={`${phase.id}-desc`} className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <phase.icon className="w-6 h-6 text-indigo-600" />
                <h4 className="font-medium">{phase.name}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">{phase.description}</p>
              {phase.activities && (
                <ul className="text-sm text-gray-600 space-y-1">
                  {phase.activities.map((activity, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{activity}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}